import { useState } from 'react';
import { getUserProfile, saveUserProfile } from '../../utils/storage';
import { updateUserProfile } from '../../services/userService';
import { reliableWrite } from '../../services/writeQueue';

const MECHANICAL_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

export default function EditProfileModal({ onClose, onToast, userId }) {
  const profile = getUserProfile();
  const [form, setForm] = useState({
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    email: profile?.email || '',
    zipCode: profile?.zipCode || '',
    yearOfBirth: profile?.yearOfBirth || '',
    mechanicalLevel: profile?.mechanicalLevel || 'Beginner',
  });
  const [saving, setSaving] = useState(false);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) return;

    setSaving(true);
    try {
      // Update localStorage (source of truth)
      const updatedProfile = {
        ...profile,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        zipCode: form.zipCode.trim(),
        yearOfBirth: form.yearOfBirth ? String(form.yearOfBirth) : '',
        mechanicalLevel: form.mechanicalLevel,
      };
      saveUserProfile(updatedProfile);

      // Reliable Supabase update (queues for retry on failure)
      if (userId) {
        const fields = {
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          zip_code: form.zipCode.trim(),
          year_of_birth: form.yearOfBirth ? Number(form.yearOfBirth) : null,
          mechanical_level: form.mechanicalLevel,
        };
        reliableWrite('profileUpdate', { userId, fields }, () =>
          updateUserProfile(userId, fields)
        );
      }

      onToast('Profile updated');
      onClose();
    } catch (err) {
      onToast(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)',
          padding: 24, width: '100%', maxWidth: 400, maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <h2 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 20, textAlign: 'center' }}>
          Edit Profile
        </h2>

        <form onSubmit={handleSave}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>First Name</label>
              <input
                className="form-input"
                type="text"
                value={form.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                style={inputStyle}
                required
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Last Name</label>
              <input
                className="form-input"
                type="text"
                value={form.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                style={inputStyle}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Email</label>
            <input
              className="form-input"
              type="email"
              value={form.email}
              disabled
              style={{ ...inputStyle, opacity: 0.5, cursor: 'not-allowed' }}
            />
            <p style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 4 }}>
              Email cannot be changed here.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Postcode / ZIP</label>
              <input
                className="form-input"
                type="text"
                maxLength={10}
                value={form.zipCode}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^A-Za-z0-9 -]/g, '').slice(0, 10).toUpperCase();
                  update('zipCode', v);
                }}
                placeholder="e.g. 90210, SW1A 1AA, K1A 0B1"
                style={{ ...inputStyle, textTransform: 'uppercase' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Year of Birth</label>
              <input
                className="form-input"
                type="text"
                inputMode="numeric"
                value={form.yearOfBirth}
                onChange={(e) => update('yearOfBirth', e.target.value)}
                placeholder="e.g. 1990"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Mechanical Level</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {MECHANICAL_LEVELS.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => update('mechanicalLevel', level)}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    borderRadius: 'var(--radius-sm)',
                    border: form.mechanicalLevel === level
                      ? '1.5px solid var(--accent)'
                      : '1.5px solid var(--border)',
                    background: form.mechanicalLevel === level
                      ? 'var(--accent-subtle)'
                      : 'var(--bg-elevated)',
                    color: form.mechanicalLevel === level
                      ? 'var(--accent)'
                      : 'var(--text-secondary)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !form.firstName.trim() || !form.lastName.trim()}
              style={{ flex: 1, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 4,
};

const inputStyle = {
  fontSize: '0.875rem',
  padding: '10px 12px',
};
